import { initRoleManagement } from '@adornis/abac/init.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { tableView } from '@adornis/data-view/client/table/table-view.js';
import { defaultERMSidebar } from '@adornis/entity-relationship-management/client/defaultERMSidebar.js';
import { defineEntity } from '@adornis/entity-relationship-management/client/define-entity.js';
import { createERMRouter } from '@adornis/entity-relationship-management/client/erm-router.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { OrganizationsAPI } from '@saraa/organizations/api/OrganizationsAPI.js';
import { Organization } from '@saraa/organizations/entities/Organization.js';
import { UserAPI } from '@saraa/permissions/api/UserAPI.js';
import type { SaraaUser } from '@saraa/permissions/db/SaraaUser.js';
import { TemplateFieldAPI } from '@saraa/template-fields/api/TemplateFieldAPI.js';
import { TemplateField } from '@saraa/template-fields/entities/TemplateField.js';
import { TemplateAPI } from '@saraa/templater/api/TemplateAPI.js';
import { Template } from '@saraa/templater/entities/Template.js';
import { html, nothing } from 'lit';
import { versionsCollectionNameMapping } from '../api/versionCollectionMapping.js';
import { openCreateUserDialog } from './CreateUserDialog.js';
import { OrganizationGenerationStatsSection } from './OrganizationGenerationStatsSection.js';
import { OrganizationMembershipsSection } from './OrganizationMembershipsSection.js';
import { OrganizationSelect } from './OrganizationSelect.js';
import { PlatformAdminSection } from './PlatformAdminSection.js';
import { pickOrganizationId } from './pickOrganization.js';
import { SessionActions } from './session-actions.js';
import { UserPasswordSection } from './UserPasswordSection.js';

/**
 * The owning organization, shown but not editable.
 *
 * Ownership is decided at creation and no endpoint will move it: it is what
 * every permission check on the entity reads, so a form that could change it
 * would be a form that could hand the row to someone else. The picker therefore
 * runs before the entity exists — see `pickOrganizationId`.
 */
const organizationField = () => ({
  // `form.field` is keyed on the concrete entity, so this is built per entity
  // definition rather than shared as one typed constant.
  // biome-ignore lint/suspicious/noExplicitAny: the section context is entity-specific
  render: ({ form }: { form?: any }) =>
    form
      ? html`
          <div class="col-span-full">
            ${OrganizationSelect({
              ...form.field('organizationId'),
              disabled: true,
              placeholder: 'The owning organization',
            })}
          </div>
        `
      : nothing,
});

/**
 * The ERM's own CRUD defaults all go through the generic entity mutations —
 * `entity.save()`, `new Entity({}).create()`, `archiveEntity` — and those are
 * closed. Every entity therefore brings its own operations: the package's
 * endpoints, each of which carries the permission check for what it does.
 *
 * `onSave` keeps the two steps the ERM default does before writing: validate the
 * form, and flush pending file uploads so a freshly picked file has an id by the
 * time the entity referencing it is saved.
 */
const saveWith =
  <T>(save: (entity: T) => Promise<unknown>) =>
  // biome-ignore lint/suspicious/noExplicitAny: FunctionalForm is keyed on the concrete entity
  async (entity: T, form: any) => {
    await form.validate();
    await form.uploadAllPendingFiles();
    await save(entity);
  };

function initEntities() {
  const organizationEntity = defineEntity(Organization, {
    label: 'Organizations',
    route: 'organizations',
    icon: 'icon-[material-symbols--corporate-fare]',
    list: {
      selectionSet: { name: 1 },
      searchFields: ['name'],
      listViews: [tableView<typeof Organization>()],
      columns: [{ fieldName: 'name', columnName: 'Name', isSortable: true }],
      operation: (skip, limit, filter) => fields => OrganizationsAPI.subscribe(skip, limit, filter)(fields),
    },
    // A placeholder name because the endpoint insists on one — the form opens on
    // the new row immediately, which is where it gets its real one.
    onCreate: () => OrganizationsAPI.create(new Organization({ name: 'New organization' }))(),
    detail: {
      title: field => field.name || 'New organization',
      operation: id => fields => OrganizationsAPI.getById(id)(fields).then(field => field ?? null),

      selectionSet: selectionSet(() => Organization, 1),
      sections: [
        {
          title: 'Organization Options',
          /**
           * `extractionPrompt` and `extractionModel` are deliberately not shown.
           * Both are per-organization overrides of the extraction defaults, and
           * exposing them invites an edit whose effect only appears later, in the
           * quality of a generated document. They stay on the entity and are still
           * honoured when set — this hides the controls, it does not remove the
           * feature.
           */
          fields: [{ fieldName: 'name' }],
        },
        {
          /**
           * Read-only, and outside the form on purpose: these are counted out of the generation logs,
           * not fields of the organization, so there is nothing here for the autosave to write back.
           */
          title: 'Generation activity',
          render: ({ entity }) =>
            entity?._id ? OrganizationGenerationStatsSection({ organizationId: entity._id }) : nothing,
        },
      ],
      onSave: saveWith((organization: Organization) => OrganizationsAPI.update(organization)()),
      onDelete: async organization => {
        await OrganizationsAPI.delete(organization._id)();
      },
      onArchive: async organization => {
        await OrganizationsAPI.setArchived(organization._id, true)();
        organization.archivedAt = new Date();
      },
      onUnarchive: async organization => {
        await OrganizationsAPI.setArchived(organization._id, false)();
        organization.archivedAt = null;
      },
    },
  });

  const templateFieldEntity = defineEntity(TemplateField, {
    label: 'Template Fields',
    route: 'template-fields',
    icon: 'icon-[material-symbols--data-object]',
    list: {
      selectionSet: { token: 1, type: 1, arrayType: 1, organizationId: 1 },
      searchFields: ['token', 'description'],
      listViews: [tableView<typeof TemplateField>()],
      columns: [
        { fieldName: 'token', columnName: 'Token', isSortable: true },
        { fieldName: 'type', columnName: 'Type', isSortable: true },
        { fieldName: 'arrayType', columnName: 'Array', isSortable: true },
      ],
      operation: (skip, limit, filter) => fields => TemplateFieldAPI.subscribe(skip, limit, filter)(fields),
    },
    // The organization is settled before the row exists — it is what every later
    // permission check reads and no endpoint will move it.
    onCreate: async () =>
      TemplateFieldAPI.create(await pickOrganizationId(), new TemplateField({ token: 'new_field' }))(),
    detail: {
      title: field => field.token || 'New template field',
      operation: id => fields => TemplateFieldAPI.getById(id)(fields).then(field => field ?? null),

      selectionSet: selectionSet(() => TemplateField, 1),
      sections: [
        {
          title: 'Template field',
          fields: [
            { fieldName: 'token' },
            { fieldName: 'description' },
            { fieldName: 'type' },
            { fieldName: 'arrayType' },
          ],
        },
        { title: 'Organization', fields: [organizationField()] },
      ],
      onSave: saveWith((templateField: TemplateField) => TemplateFieldAPI.update(templateField)()),
      onDelete: async templateField => {
        await TemplateFieldAPI.delete(templateField._id)();
      },
      onArchive: async templateField => {
        await TemplateFieldAPI.setArchived(templateField._id, true)();
        templateField.archivedAt = new Date();
      },
      onUnarchive: async templateField => {
        await TemplateFieldAPI.setArchived(templateField._id, false)();
        templateField.archivedAt = null;
      },
    },
  });

  const templateEntity = defineEntity(Template, {
    label: 'Templates',
    route: 'templates',
    icon: 'icon-[material-symbols--description-outline]',
    list: {
      selectionSet: selectionSet(() => Template, 1),
      searchFields: ['name', 'description'],
      listViews: [tableView<typeof Template>()],
      columns: [
        { fieldName: 'name', columnName: 'Name', isSortable: true },
        { fieldName: 'description', columnName: 'Description' },
      ],
      operation: (skip, limit, filter) => fields => TemplateAPI.subscribe(skip, limit, filter)(fields),
    },
    // The organization is settled before the row exists — it is what every later
    // permission check reads and no endpoint will move it.
    onCreate: async () => TemplateAPI.create(await pickOrganizationId(), new Template({ name: 'New template' }))(),
    detail: {
      title: template => template.name || 'New template',
      operation: id => fields => TemplateAPI.getById(id)(fields).then(template => template ?? null),
      selectionSet: selectionSet(() => Template, 1),
      sections: [
        { title: 'Details', fields: [{ fieldName: 'name' }, { fieldName: 'description' }] },
        { title: 'Organization', fields: [organizationField()] },
        {
          // The one-to-one .docx template file. Uploaded via the form's file
          // registry, so the ERM save flow (uploadAllPendingFiles) persists it.
          title: 'Template file (.docx)',
          fields: [
            {
              render: ({ form }) =>
                form
                  ? html`
                      <div class="col-span-full">
                        ${FileInput({
                          ...form.field('templateFileId'),
                          accept: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          placeholder: 'Upload .docx template',
                          // Not `clearable`: this field holds exactly one file, and the chip
                          // already carries its own remove. The clear affordance on top of it
                          // put two ✕ next to each other doing the same thing.
                          clearable: false,
                          placeholderMode: 'hide',
                          onFilesChanged: ({ pendingFiles }) => {
                            if (pendingFiles.length) setTimeout(() => void form.uploadAllPendingFiles(), 0);
                          },
                        })}
                      </div>
                    `
                  : nothing,
            },
          ],
        },
      ],
      onSave: saveWith((template: Template) => TemplateAPI.update(template)()),
      onDelete: async template => {
        await TemplateAPI.delete(template._id)();
      },
      onArchive: async template => {
        await TemplateAPI.setArchived(template._id, true)();
        template.archivedAt = new Date();
      },
      onUnarchive: async template => {
        await TemplateAPI.setArchived(template._id, false)();
        template.archivedAt = null;
      },
    },
  });

  return { templateEntity, templateFieldEntity, organizationEntity };
}

export function initializeErm() {
  const { templateEntity, templateFieldEntity, organizationEntity } = initEntities();

  initRoleManagement({});

  const ermRouter = createERMRouter({
    entities: [templateEntity, templateFieldEntity, organizationEntity],

    renderSidebar: defaultERMSidebar({
      logo: () => html`
        <div class="flex items-center justify-center">
          <button
            class="rounded-box focus-visible:outline-primary flex cursor-pointer items-center justify-center p-3 transition-transform duration-200 hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2"
            type="button"
            aria-label="Back to document generation"
            @click=${() => goTo(`/`)}
          >
            <img src="${require('../assets/logo_LeafTrust.png')}" alt="LeafTrust" class="h-20 w-auto object-contain" />
          </button>
        </div>
      `,

      // Below the menu, where the account the session belongs to is out of the way
      // of the navigation but still reachable from every admin page.
      footer: () => SessionActions({ layout: 'column' }),
    }),

    adminPages: {
      userManagement: {
        /**
         * The sidebar label for this page is *not* set here — `label` on an admin page is
         * not what the sidebar reads. It renders `m['_ermRouter_page_' + key]() ?? key`, and
         * the ERM ships no `_ermRouter_page_userManagement`, so it fell through to the raw
         * config key and the nav read `userManagement`.
         *
         * `useTranslationContext` merges this app's global messages over the package's, so
         * the fix is to supply that key ourselves: it lives in
         * `src/localization/messages/en.json` beside the `_field_*` overrides already
         * feeding this page's form labels.
         *
         * There is no icon here for the same reason — the sidebar's admin-route branch
         * renders no icon element at all, unlike the entity branch above it.
         */
        onCreate: openCreateUserDialog,
        list: {
          selectionSet: { _id: 1, _class: 1, email: 1, username: 1, archivedAt: 1 },
          searchFields: ['email', 'username'],
          listViews: [tableView<typeof SaraaUser>()],
          columns: [
            { fieldName: 'email', columnName: 'Email', isSortable: true },
            { fieldName: 'username', columnName: 'Username', isSortable: true },
          ],
          operation: (skip, limit, filter) => fields => UserAPI.subscribe(skip, limit, filter)(fields),
        },
        detail: {
          /**
           * Spelled out rather than merged into the defaults.
           *
           * `Change.deepMerge` merges arrays *element-wise when the lengths
           * match* — and the ERM default has exactly three sections, so a
           * three-element override here would mutate the package's shared
           * `authenticationSection` / `roleSection` singletons in place. Four
           * entries sidesteps that.
           *
           * Note what is deliberately absent: `_class` and `title`. `deepMerge`
           * walks a class's enumerable statics, so passing `_class: SaraaUser`
           * would execute `AdornisUser._class = 'SaraaUser'` on the global class
           * object at import time. It is also unnecessary — `UserAPI` reads both
           * classes out of the shared collection.
           */
          sections: [
            // English like the rest of this app. The ERM's own sections around it are
            // translated by the package and follow the locale pinned in `initLocale`;
            // this one is ours, so it has to be written in the language we ship.
            { title: 'Account', fields: [{ fieldName: 'email' }, { fieldName: 'username' }] },
            {
              title: 'Organizations',
              // Passed only the id: memberships are deliberately outside this
              // form, so the autosave can never write the array. See the section
              // for why.
              render: ({ entity }) => (entity?._id ? OrganizationMembershipsSection({ userId: entity._id }) : nothing),
            },

            /**
             * Not the ERM's `authenticationSection`. That one calls the engine's
             * `changePassword`, which asserts the legacy `Users.ChangePassword`
             * against `user.roles` — and roles are gone, so every reset failed
             * with "Permission 'Users.ChangePassword' not granted", for platform
             * admins included. Its impersonation half fails the same way and is
             * not carried over; see `UserPasswordSection`.
             */
            UserPasswordSection,
            /**
             * Not the ERM's `roleSection`. That component subscribes to the role
             * and role-assignment collections the moment it renders, and both are
             * gone. It also offered a dropdown that could revoke your own platform
             * admin in one click. `PlatformAdminSection` is the single decision
             * that is actually left.
             */
            // No `title`: the section renders nothing at all for anyone who is not
            // a platform admin, and a bare heading over empty space reads as
            // something failing to load.
            { render: PlatformAdminSection.render },
          ],
          /**
           * `memberships` is deliberately absent — it is not something this form
           * has any business carrying. `updateUser` would ignore it either way
           * (it is not an editable field), but not loading it keeps the form
           * honest about what it can change.
           */
          selectionSet: { _id: 1, _class: 1, email: 1, username: 1, archivedAt: 1 },
          operation: id => fields => {
            return UserAPI.getById(id)(fields).then(user => user ?? null);
          },
          onSave: saveWith((user: SaraaUser) => UserAPI.update(user)()),
          onDelete: async user => {
            await UserAPI.delete(user._id)();
          },
          onArchive: async user => {
            await UserAPI.setArchived(user._id, true)();
            user.archivedAt = new Date();
          },
          onUnarchive: async user => {
            await UserAPI.setArchived(user._id, false)();
            user.archivedAt = null;
          },
        },
      },

      /**
       * `roleManagement` is deliberately absent, which is what keeps the route
       * from existing at all — `createERMRouter` only mounts it when configured.
       *
       * The page it would mount is the ERM permission matrix, the create-role view
       * and the permission tester, all of which read and write the `roles` and
       * `role-assignments` collections directly. Those are gone: the grant table
       * lives in `@saraa/permissions/grants.ts`, in git and reviewed, rather than
       * in documents that could be edited in production without attribution.
       */
    },

    renderForbiddenPage: () => html`
      <div class="flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center">
        <span class="icon-[material-symbols--lock-outline] text-base-content/30 text-5xl"></span>
        <h1 class="text-base-content text-xl font-semibold">No access</h1>
        <p class="text-base-content/60 max-w-sm text-pretty">
          Your account does not have permission to open this page. Ask an administrator if you think this is wrong.
        </p>
        <a href="/" class="btn btn-primary btn-sm gap-2">
          <span class="icon-[material-symbols--auto-awesome] text-base"></span> Go to document generation
        </a>
      </div>
    `,

    getVersionsCollectionName: entity => {
      const mapping = versionsCollectionNameMapping[(entity as any)._collectionName];
      if (!mapping) return '';
      return typeof mapping === 'string' ? mapping : mapping.versionedCollectionName;
    },
  });

  const renderERM = () =>
    html`<div class="flex h-full w-full flex-col overflow-hidden">
      <header class="navbar bg-base-100/85 border-base-300 shrink-0 border-b px-4 backdrop-blur">
        <!--
          Offset past the sidebar the ERM renders below this bar, so the title lines up with
          the content column rather than sitting over the logo. It mirrors the sidebar's own
          \`w-64\` — the package owns that width, so the two have to be kept in step by hand.
        -->
        <div class="ml-64 flex min-w-0 flex-none flex-col">
          <h1 class="text-base-content text-base leading-tight font-semibold">Admin</h1>
          <p class="text-base-content/60 truncate text-xs">Templates, fields, organizations and users</p>
        </div>
        <div class="flex-1"></div>
        <a href="/" class="btn btn-ghost btn-sm shrink-0 gap-1.5">
          <span class="icon-[material-symbols--auto-awesome] text-base"></span>
          Document generation
        </a>
      </header>
      ${ermRouter.outlet.value}
    </div>`;

  return { ermRouter, renderERM };
}
